/**
 * 侧边栏导航组件
 * 支持展开/收缩，包含所有功能入口
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DocumentPlusIcon,
  FolderIcon,
  DocumentDuplicateIcon,
  BuildingOfficeIcon,
  LinkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  UsersIcon,
  UserGroupIcon,
  BellIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { UserInfo } from '../types';
import { configApi, authApi, notificationApi, companyApi } from '../services/api';
import { getModelDisplayName, filterModels, filterVisionModels, TEXT_MODELS_CONFIG } from '../config/aiConfig';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import NotificationCenter from './NotificationCenter';
import { ChangelogDialog, APP_VERSION } from './ChangelogDialog';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  currentUser: UserInfo;
  onLogout: () => void;
}

// 文本模型关键词（用于过滤模型列表）
const TEXT_MODEL_KEYWORDS = ['gpt', 'claude', 'qwen', 'deepseek', 'chat', 'llama', 'text'];
const VISION_MODEL_KEYWORDS = ['vl', 'vision', 'gpt-4o', 'claude-3'];

// 根据编译版本显示不同的标题
const APP_TITLE = process.env.REACT_APP_COMPANY_VERSION === 'true' 
  ? '北区政企客户部' 
  : '智能标书生成助手';

const Sidebar: React.FC<SidebarProps> = ({
  activeView,
  onViewChange,
  currentUser,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 根据当前路由自动设置 activeView
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === '/tokens') return 'token-management';
    if (path === '/projects') return 'project-management';
    if (path.startsWith('/project-groups')) return 'project-group-management';
    if (path === '/create' || path.startsWith('/project/')) return 'new-project';
    if (path.startsWith('/templates')) return 'template-management';
    if (path.startsWith('/companies')) return 'company-management';
    if (path.startsWith('/resources')) return 'resource-management';
    return activeView;
  };
  
  const currentActiveView = getCurrentView();
  
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [textModel, setTextModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [textModels, setTextModels] = useState<string[]>([]);
  const [visionModels, setVisionModels] = useState<string[]>([]);
  // 使用currentUser.name（优先display_name，其次username）
  const [username, setUsername] = useState(currentUser.name);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [showCompanyConfirm, setShowCompanyConfirm] = useState(false);
  
  // 消息通知状态
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  
  // Toast状态
  const [toast, setToast] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });

  // 加载未读消息数量
  const loadUnreadCount = async () => {
    // 检查用户是否登录
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const res = await notificationApi.getUnreadCount();
      setUnreadCount(res.data?.unread_count || 0);
    } catch (error) {
      // 静默处理错误，不打印日志避免干扰
    }
  };

  // 组件挂载时加载未读数量（不轮询，只在页面刷新或切换模块时请求）
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // 检查用户是否有主体
  useEffect(() => {
    const checkCompany = async () => {
      try {
        const response = await companyApi.getCompanies();
        setHasCompany(response.data && response.data.length > 0);
      } catch (e) {
        setHasCompany(false);
      }
    };
    checkCompany();
    
    // 监听主体更新事件，刷新主体状态
    const handleCompanyUpdate = () => {
      checkCompany();
    };
    window.addEventListener('company:updated', handleCompanyUpdate);
    
    return () => {
      window.removeEventListener('company:updated', handleCompanyUpdate);
    };
  }, []);

  // 加载可用模型列表和用户设置
  useEffect(() => {
    loadAvailableModels();
    loadUserSettings();
  }, []);

  // 监听currentUser变化，同步更新username状态
  useEffect(() => {
    setUsername(currentUser.name);
  }, [currentUser.name]);

  const loadAvailableModels = async () => {
    try {
      setLoadingModels(true);
      
      // 从后端获取可用模型列表（后端使用本地配置代理请求）
      const response = await configApi.getModels();
      
      if (response.data.success && response.data.models) {
        const models = response.data.models;
        setAvailableModels(models);
        
        // 过滤文本模型和视觉模型（只保留配置中的模型）
        const text = filterModels(models);
        const vision = filterVisionModels(models);
        
        setTextModels(text);
        setVisionModels(vision);
        
        // 如果没有设置过视觉模型，默认选择第一个可用的视觉模型
        if (vision.length > 0 && !visionModel) {
          setVisionModel(vision[0]);
        }
        // 如果没有设置过文本模型，默认选择第一个可用的文本模型
        if (text.length > 0 && !textModel) {
          setTextModel(text[0]);
        }
      }
    } catch (error) {
      console.error('加载模型列表失败:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  // 加载用户设置
  const loadUserSettings = async () => {
    try {
      const response = await authApi.getSettings();
      if (response.data) {
        const { text_model, vision_model, display_name } = response.data;
        if (text_model) setTextModel(text_model);
        if (vision_model) setVisionModel(vision_model);
        // 如果后端返回了display_name，使用它；否则保持当前值
        if (display_name) {
          setUsername(display_name);
        }
      }
    } catch (error) {
      console.error('加载用户设置失败:', error);
    }
  };

  // 保存用户设置
  const saveUserSettings = async () => {
    try {
      await authApi.updateSettings({
        text_model: textModel,
        vision_model: visionModel,
        display_name: username
      });
      
      // 更新localStorage中的用户信息
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          user.display_name = username;
          localStorage.setItem('user', JSON.stringify(user));
          
          // 触发用户信息更新事件，通知App.tsx刷新
          window.dispatchEvent(new CustomEvent('user:updated', { 
            detail: { display_name: username } 
          }));
        } catch (e) {
          console.error('Failed to update user in localStorage:', e);
        }
      }
      
      // 关闭弹窗
      setShowSettings(false);
      
      // 显示成功提示
      setToast({
        show: true,
        type: 'success',
        message: '设置保存成功'
      });
      
      return true;
    } catch (error) {
      console.error('保存用户设置失败:', error);
      
      // 显示失败提示
      setToast({
        show: true,
        type: 'error',
        message: '设置保存失败，请稍后重试'
      });
      
      return false;
    }
  };

  // 处理退出登录
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  // 处理新建标书点击
  const handleNewProject = () => {
    if (hasCompany === false) {
      setShowCompanyConfirm(true);
    } else {
      navigate('/create');
    }
  };

  // 导航菜单项（含角色可见性控制）
  // super: 全部菜单
  // admin: 新建标书、项目管理、主体管理、Token管理
  // user:  新建标书、项目管理、主体管理、Token管理
  const allMenuItems = [
    {
      id: 'new-project',
      name: '新建标书',
      icon: DocumentPlusIcon,
      onClick: handleNewProject,
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'project-management',
      name: '项目管理',
      icon: FolderIcon,
      onClick: () => navigate('/projects'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'project-group-management',
      name: '项目组管理',
      icon: UserGroupIcon,
      onClick: () => navigate('/project-groups'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'company-management',
      name: '主体管理',
      icon: BuildingOfficeIcon,
      onClick: () => navigate('/companies'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'template-management',
      name: '模板管理',
      icon: DocumentDuplicateIcon,
      onClick: () => navigate('/templates'),
      roles: ['super'],  // 仅超级管理员
    },
    {
      id: 'resource-management',
      name: '资源管理',
      icon: LinkIcon,
      onClick: () => navigate('/resources'),
      roles: ['super'],  // 仅超级管理员
    },
    {
      id: 'user-management',
      name: '人员管理',
      icon: UsersIcon,
      onClick: () => navigate('/users'),
      roles: ['super', 'admin'],  // 仅管理员及以上
      divider: true,  // 在此项前显示分割线
    },
    {
      id: 'token-management',
      name: 'Token管理',
      icon: CurrencyDollarIcon,
      onClick: () => navigate('/tokens'),
      roles: ['super', 'admin', 'user'],
      divider: true,  // 普通用户看不到人员管理，Token管理对他们是第一个系统菜单
    },
  ];
  const menuItems = allMenuItems.filter(item => item.roles.includes(currentUser.role));
  // 标记过滤后第一个带 divider 的菜单项（避免普通用户 Token 前重复显示分割线）
  const firstDividerId = menuItems.find(item => (item as any).divider)?.id;

  // 文字淡入：展开时延迟150ms后淡入，收起时立即隐藏
  const textClass = isExpanded
    ? 'opacity-100 transition-opacity duration-150 delay-150'
    : 'opacity-0 transition-opacity duration-75';

  return (
    <div
      className={`flex flex-col transition-all duration-300 relative overflow-hidden h-full ${
        isExpanded ? 'w-[226px]' : 'w-16'
      }`}
      style={{
        background: 'linear-gradient(160deg, #eff6ff 0%, #eef2ff 40%, #faf5ff 75%, #fdf4ff 100%)',
      }}
    >
      {/* 装饰性光晕背景 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* 右上角蓝紫光晕 */}
        <div style={{
          position: 'absolute', top: '-30px', right: '-30px',
          width: '160px', height: '160px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.28) 0%, transparent 70%)',
        }} />
        {/* 中中部紫色光晕 */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '140px', height: '140px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168,85,247,0.24) 0%, transparent 70%)',
        }} />
        {/* 左下角青蓝光晕 */}
        <div style={{
          position: 'absolute', bottom: '-20px', left: '-20px',
          width: '140px', height: '140px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.24) 0%, transparent 70%)',
        }} />
      </div>
      <div className="px-3 py-4 flex items-center flex-shrink-0 overflow-hidden" style={{ borderBottom: '1px solid rgba(148,130,246,0.12)' }}>
        {isExpanded ? (
          <>
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md flex-shrink-0">
                <span className="text-white text-xs font-bold">AI</span>
              </div>
              <div className="min-w-0 overflow-hidden">
                <h1 className="text-sm font-bold text-gray-800 leading-tight whitespace-nowrap">AI标书助手</h1>
                <p className="text-[10px] text-blue-500 leading-tight whitespace-nowrap">{APP_TITLE}</p>
              </div>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-1"
              title="收起侧边栏"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsExpanded(true)}
            className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md hover:from-blue-400 hover:to-indigo-500 transition-all mx-auto flex-shrink-0"
            title="展开侧边栏"
          >
            <span className="text-white text-xs font-bold">AI</span>
          </button>
        )}
      </div>

      {/* 菜单列表 */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
        <ul className="space-y-0.5 px-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentActiveView === item.id;
            const showDivider = item.id === firstDividerId;

            return (
              <li key={item.id}>
                {/* 分割线 */}
                {showDivider && (
                  <div className="flex items-center gap-2 my-3 px-1">
                    <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(148,130,246,0.3), transparent)' }} />
                    {isExpanded && (
                      <span className={`text-[11px] font-semibold tracking-widest uppercase whitespace-nowrap ${textClass}`} style={{ color: 'rgba(148,130,246,0.7)' }}>系统管理</span>
                    )}
                    <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(148,130,246,0.3), transparent)' }} />
                  </div>
                )}
                <button
                  onClick={item.onClick}
                  title={!isExpanded ? item.name : undefined}
                  className={`w-full flex items-center rounded-xl transition-all duration-200 group py-2.5 ${
                    isExpanded ? 'justify-start gap-2.5 px-3' : 'justify-center px-0'
                  } ${
                    isActive
                      ? 'text-white shadow-md'
                      : 'text-slate-500 hover:text-indigo-700'
                  }`}
                  style={isActive ? {
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.85) 0%, rgba(99,102,241,0.85) 100%)',
                    boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
                  } : undefined}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, rgba(59,130,246,0.13) 0%, rgba(99,102,241,0.13) 100%)'; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = ''; }}
                >
                  <Icon className={`w-6 h-6 flex-shrink-0 transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-110'}`} />
                  {isExpanded && (
                    <>
                      <span className={`text-[15px] font-medium whitespace-nowrap overflow-hidden ${textClass}`}>{item.name}</span>
                      {/* 激活时右侧绿点 */}
                      {isActive && (
                        <div className={`ml-auto w-2 h-2 rounded-full bg-green-400 flex-shrink-0 ${textClass}`} style={{ boxShadow: '0 0 6px rgba(74,222,128,0.6)' }} />
                      )}
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* 底部版本号（在分割线上方） */}
      <div className="flex-shrink-0 flex justify-center py-1">
        <button
          onClick={() => setShowChangelog(true)}
          className="text-[11px] text-gray-400 hover:text-blue-500 transition-colors cursor-pointer"
          title="查看版本更新记录"
        >
          {APP_VERSION}
        </button>
      </div>

      {/* 用户信息区域 */}
      <div className="flex-shrink-0 px-3 py-2" style={{ borderTop: '1px solid rgba(148,130,246,0.12)' }}>
        {isExpanded ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2.5 flex-1 min-w-0 px-2 py-2 rounded-xl hover:bg-gray-100 transition-colors cursor-default">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow">
                {currentUser.avatar ? (
                  <img src={currentUser.avatar} alt={currentUser.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <UserCircleIcon className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm font-medium text-gray-800 truncate whitespace-nowrap">{currentUser.name}</p>
                <p className="text-[11px] text-slate-400 truncate whitespace-nowrap">
                  {currentUser.role === 'super' ? '超级管理员' : currentUser.role === 'admin' ? '管理员' : '普通用户'}
                </p>
              </div>
            </div>
            {/* 消息通知按钮 */}
            <button
              onClick={() => setShowNotificationCenter(true)}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                unreadCount > 0 
                  ? 'text-red-500 hover:text-red-600 hover:bg-red-50' 
                  : 'text-slate-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="消息通知"
            >
              {unreadCount > 0 ? (
                <BellAlertIcon className="w-5 h-5" />
              ) : (
                <BellIcon className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-slate-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="系统设置"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {/* 消息通知按钮（收缩状态） */}
            <button
              onClick={() => setShowNotificationCenter(true)}
              className={`p-2 rounded-lg transition-colors ${
                unreadCount > 0 
                  ? 'text-red-500 hover:text-red-600 hover:bg-red-50' 
                  : 'text-slate-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="消息通知"
            >
              {unreadCount > 0 ? (
                <BellAlertIcon className="w-5 h-5" />
              ) : (
                <BellIcon className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-slate-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="系统设置"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* 系统设置弹窗 */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">系统设置</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 弹窗内容 */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* 模型设置 */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                  模型设置
                </h3>
                
                {/* 文本模型 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    文本模型（文件解析和内容生成）
                  </label>
                  <select
                    value={textModel}
                    onChange={(e) => setTextModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loadingModels || textModels.length === 0}
                  >
                    {TEXT_MODELS_CONFIG.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} ({m.company})</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {loadingModels ? '正在获取模型列表...' : `共 ${textModels.length} 个文本模型`}
                  </p>
                </div>

                {/* 视觉模型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    视觉模型（图像识别和生成）
                  </label>
                  <select
                    value={visionModel}
                    onChange={(e) => setVisionModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loadingModels || visionModels.length === 0}
                  >
                    {visionModels.length > 0 ? (
                      visionModels.map((model) => (
                        <option key={model} value={model}>{getModelDisplayName(model)}</option>
                      ))
                    ) : (
                      <option value="">{loadingModels ? '加载中...' : '暂无可用模型'}</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {loadingModels ? '正在获取模型列表...' : `共 ${visionModels.length} 个视觉模型`}
                  </p>
                </div>
              </div>

              {/* 分割线 */}
              <div className="border-t border-gray-200 my-6"></div>

              {/* 用户设置 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                  用户设置
                </h3>
                
                {/* 显示账号 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    账号
                  </label>
                  <input
                    type="text"
                    value={currentUser.username}
                    disabled
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* 修改用户名 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    用户名
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                {/* 角色显示 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    角色
                  </label>
                  <input
                    type="text"
                    value={currentUser.role === 'super' ? '超级管理员' : currentUser.role === 'admin' ? '管理员' : '普通用户'}
                    disabled
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* 退出登录按钮 */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  退出登录
                </button>
              </div>
            </div>

            {/* 弹窗底部 */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveUserSettings}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 退出登录确认对话框 */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="确认退出登录？"
        message="您确定要退出登录吗？退出后将返回到登录页面。"
        confirmText="确认退出"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
      
      {/* Toast通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.message}
        duration={3000}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* 消息通知中心 */}
      <NotificationCenter
        isOpen={showNotificationCenter}
        onClose={() => {
          setShowNotificationCenter(false);
          loadUnreadCount(); // 关闭时刷新未读数量
        }}
      />

      {/* 版本记录弹框 */}
      <ChangelogDialog isOpen={showChangelog} onClose={() => setShowChangelog(false)} />

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={showCompanyConfirm}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setShowCompanyConfirm(false);
          navigate('/companies/new');
        }}
        onCancel={() => {
          setShowCompanyConfirm(false);
          navigate('/create');
        }}
      />
    </div>
  );
};

export default Sidebar;
